import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import debounce from 'lodash/debounce';
import trim from 'lodash/trim';
import { useSearchParams } from 'react-router-dom';
import { useIsomorphicLayoutEffect } from 'react-use';
import { BusinessCollection } from '../../data-layer/business/constants';
import { MOBILE_FOOTER_HEIGHT } from '../../ui/dashboard/DashboardLayout';
import { DashboardPage } from '../../ui/dashboard/DashboardPage';
import { IconClose } from '../../ui/icon/IconClose';
import { IconButton } from '../../ui/input/IconButton';
import { Input } from '../../ui/input/Input';
import { Box } from '../../ui/layout/Box';
import { DataTestId } from '../../ui/layout/DataTestIdWrapper';
import { Flex } from '../../ui/layout/Flex';
import { Tab, Tabs } from '../../ui/navigation/Tabs';
import { Typography } from '../../ui/typography/Typography';
import { useEventSource } from '../../utils/LocationContext';
import { capitalizeFirstLetter } from '../../utils/capitalizeFirstLetter';
import { getWindow } from '../../utils/getWindow';
import {
    getPageNameByEventSource,
    ymTrackSearchInputFocusOut,
    ymTrackSearchInputStart,
    ymTrackSearchOpen,
    ymTrackSwitchSearchType,
} from '../../utils/metrika';
import { useMeta } from '../../utils/useMeta';
import { useTranslation } from '../../utils/useTranslation';
import { useBusinessCollection } from '../business/useBusinessCollection';
import { usePostCollection } from '../post/usePostCollection';
import { useCurrentUser } from '../users/useCurrentUser';
import { SearchBusiness } from './SearchBusiness';
import { SearchPost } from './SearchPost';
import { prepareSearchScrollRestoration } from './searchScrollRestoration';

const TIMEOUT = 250;
const PER_PAGE = 24;

export const Search = () => {
    const [query, setQuery] = useSearchParams();
    const fulltextQuery = query.get('query') ?? '';
    const normalizedQuery = trim(fulltextQuery);
    const searchTypeQuery = query.get('searchType') ?? 'post';
    const currentUser = useCurrentUser();
    const t = useTranslation();
    const firstInputActionHappenedRef = useRef(false);
    const { eventSource } = useEventSource();

    const onChangeQuery = useCallback(
        (queryText: string) =>
            setQuery({ searchType: searchTypeQuery, query: queryText }, { replace: true }),
        [setQuery, searchTypeQuery],
    );

    const onChangeTab = useCallback(
        (newSearchType: string) => {
            setQuery({ searchType: newSearchType, query: fulltextQuery }, { replace: true });

            if (!eventSource) {
                return;
            }

            ymTrackSwitchSearchType({
                page: getPageNameByEventSource(eventSource),
                userLogin: currentUser?.login,
                businessId: currentUser?.profile?.publicId,
                value: newSearchType,
            });
        },
        [setQuery, fulltextQuery, currentUser, eventSource],
    );

    const businessCollection = useBusinessCollection({
        namespace: BusinessCollection.SEARCH,
    });
    const postCollection = usePostCollection({
        namespace: 'search-collection',
        filters: {
            amount: PER_PAGE,
            query: fulltextQuery,
        },
    });

    useIsomorphicLayoutEffect(() => {
        prepareSearchScrollRestoration(normalizedQuery);
    }, [normalizedQuery]);

    const onBusinessSearch = useMemo(
        () =>
            debounce(
                (queryText: string = '') =>
                    businessCollection.onLoadNext({ query: trim(queryText), limit: 20 }),
                TIMEOUT,
            ),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [businessCollection.onLoadNext],
    );

    const onPostSearch = useMemo(
        () =>
            debounce(
                (queryText: string = '') =>
                    postCollection.onUpdateQuery({
                        amount: PER_PAGE,
                        query: queryText,
                    }),
                TIMEOUT,
            ),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [postCollection.onUpdateQuery],
    );

    useEffect(() => {
        if (
            businessCollection.filters.query !== trim(fulltextQuery) &&
            !businessCollection.isLoading
        ) {
            onBusinessSearch(fulltextQuery);
        }
    }, [
        fulltextQuery,
        businessCollection.isLoading,
        businessCollection.filters.query,
        onBusinessSearch,
    ]);

    useEffect(() => {
        if (
            trim(postCollection.filters?.query) !== trim(fulltextQuery) &&
            !postCollection.isLoading
        ) {
            onPostSearch(fulltextQuery);
        }
    }, [
        fulltextQuery,
        postCollection,
        postCollection.onUpdateQuery,
        postCollection.isLoading,
        onPostSearch,
    ]);

    useEffect(() => () => {
        getWindow()?.sessionStorage.setItem('lastSearch', fulltextQuery ?? '');
    });

    useEffect(() => {
        if (!currentUser || !eventSource) {
            return;
        }

        ymTrackSearchOpen({
            page: getPageNameByEventSource(eventSource),
            userLogin: currentUser?.login,
            businessId: currentUser?.profile?.publicId,
        });
    }, [currentUser, eventSource]);

    useMeta({
        title: fulltextQuery
            ? `${capitalizeFirstLetter(fulltextQuery)} в Яндекс Ритме`
            : 'Посты для вдохновения в сервисе Яндекс Ритм',
        description: fulltextQuery
            ? `Интересные посты для вдохновения на тему ${fulltextQuery} в Яндекс Ритме`
            : 'Яндекс Ритм — найти, вдохновиться и купить.',
        metas:
            postCollection.items.length < PER_PAGE
                ? [
                      {
                          name: 'robots',
                          content: 'noindex, nofollow',
                      },
                  ]
                : undefined,
    });

    return (
        <DashboardPage
            height={{ xs: `calc(100dvh - ${MOBILE_FOOTER_HEIGHT}px)`, sm: '100vh' }}
            gridFeed
        >
            <Flex width="100%" minHeight="100%" py={{ sm: 2 }}>
                <Flex
                    p={2}
                    pb={{ xs: 0, sm: 2 }}
                    mx={{ lg: -2 }}
                    boxSizing={{ lg: 'content-box' }}
                    width="100%"
                    minHeight="100%"
                    direction="column"
                    alignItems="stretch"
                    borderRadius={{ sm: 2 }}
                    background="var(--background-primary)"
                >
                    <Box pb={1}>
                        <Input
                            value={query.get('query') ?? ''}
                            variant="filled"
                            placeholder="Поиск"
                            dataTestId={DataTestId.SEARCH_INPUT}
                            onBlur={(event) => {
                                if (!eventSource) {
                                    return;
                                }

                                ymTrackSearchInputFocusOut({
                                    page: getPageNameByEventSource(eventSource),
                                    userLogin: currentUser?.login,
                                    businessId: currentUser?.profile?.publicId,
                                    value: event.target.value,
                                });
                            }}
                            endAdornment={
                                query.get('query') ? (
                                    <Box alignSelf="center">
                                        <IconButton
                                            size="xxs"
                                            variant="filled"
                                            onClick={() => onChangeQuery('')}
                                            dataTestId={DataTestId.CROSS_BUTTON}
                                        >
                                            <IconClose />
                                        </IconButton>
                                    </Box>
                                ) : undefined
                            }
                            onInput={(event) => {
                                onChangeQuery(event.currentTarget.value);

                                // Отправляем событие ввода только один раз
                                if (!firstInputActionHappenedRef.current && eventSource) {
                                    ymTrackSearchInputStart({
                                        page: getPageNameByEventSource(eventSource),
                                        userLogin: currentUser?.login,
                                        businessId: currentUser?.profile?.publicId,
                                    });

                                    firstInputActionHappenedRef.current = true;
                                }
                            }}
                        />
                    </Box>
                    <Tabs
                        px={{ xs: 1, sm: 3 }}
                        overflow="visible"
                        value={searchTypeQuery}
                        onChange={onChangeTab}
                    >
                        <Tab width="50%" value="post" textDecoration="none">
                            <Typography
                                color="inherit"
                                variant="body"
                                weight="medium"
                                textAlign="center"
                            >
                                {t('Посты')}
                            </Typography>
                        </Tab>
                        <Tab
                            width="50%"
                            value="business"
                            textDecoration="none"
                            dataTestId={DataTestId.BUSINESS_PROFILES}
                        >
                            <Typography
                                color="inherit"
                                variant="body"
                                weight="medium"
                                textAlign="center"
                            >
                                {t('Профили')}
                            </Typography>
                        </Tab>
                    </Tabs>
                    {searchTypeQuery === 'post' ? (
                        <SearchPost postCollection={postCollection} query={fulltextQuery} />
                    ) : (
                        <SearchBusiness query={fulltextQuery} />
                    )}
                </Flex>
            </Flex>
        </DashboardPage>
    );
};
